import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { PromptDialog } from "@/components/PromptDialog";
import {
  ImageIcon, CalendarClock, Eye, MessageSquare, RefreshCw, Check, Loader2,
  ThumbsUp, PencilLine, History, Trash2, Wand2, Send, ExternalLink, BellRing,
  FileDown, Columns2, X, BookmarkPlus, ChevronDown,
} from "lucide-react";

const fmt = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso; } };
const ORIGIN = typeof window !== "undefined" ? window.location.origin : "";

export function PreviewManager({ token, projectId }) {
  const [stats, setStats] = useState(null);
  const [opts, setOpts] = useState(null);
  const [showSwap, setShowSwap] = useState(false);
  const [customUrl, setCustomUrl] = useState("");
  const [logoUrl, setLogoUrl] = useState("");
  const [expiry, setExpiry] = useState("");
  const [nudgeDays, setNudgeDays] = useState("");
  const [busy, setBusy] = useState("");
  const [history, setHistory] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [tpls, setTpls] = useState(null);
  const [tplOpenFor, setTplOpenFor] = useState(null);
  const [compare, setCompare] = useState(null); // {a, b}

  const loadStats = async () => {
    try { const { data } = await api.get(`/ai/studio/preview/${token}/stats`); setStats(data); setExpiry(data.expires_at ? data.expires_at.slice(0, 10) : ""); setNudgeDays(data.nudge_days == null ? "" : String(data.nudge_days)); }
    catch { /* ignore */ }
  };
  const loadHistory = async () => {
    if (!projectId) { setHistory([]); return; }
    try { const { data } = await api.get(`/ai/studio/previews`, { params: { project_id: projectId } }); setHistory(data.previews || []); }
    catch { setHistory([]); }
  };
  const loadTpls = async () => {
    try { const { data } = await api.get(`/ai/studio/reply-templates`); setTpls(data.templates || []); } catch { setTpls([]); }
  };
  useEffect(() => { if (token) { loadStats(); loadHistory(); } /* eslint-disable-next-line */ }, [token, projectId]);

  const openSwap = async () => {
    setShowSwap((s) => !s);
    if (!opts) { try { const { data } = await api.get("/ai/studio/preview-image-options"); setOpts(data.options || []); } catch { setOpts([]); } }
  };
  const applyHero = async (url) => {
    if (!url || !url.startsWith("http")) { toast.error("Enter a valid image URL"); return; }
    setBusy("hero");
    try { await api.post(`/ai/studio/preview/${token}/image`, { hero: url }); toast.success("Hero image updated"); setCustomUrl(""); await loadStats(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };
  const applyLogo = async (url) => {
    setBusy("logo");
    try { await api.post(`/ai/studio/preview/${token}/branding`, { logo: url || null }); toast.success(url ? "Logo updated" : "Logo cleared"); setLogoUrl(""); await loadStats(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };
  const saveExpiry = async () => {
    setBusy("expiry");
    try { const { data } = await api.post(`/ai/studio/preview/${token}/expiry`, { expires_at: expiry || null }); toast.success(data.expires_at ? "Expiry saved" : "Expiry cleared — link never expires"); await loadStats(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };
  const saveNudge = async () => {
    setBusy("nudge");
    const days = nudgeDays === "" ? null : Number(nudgeDays);
    try { await api.post(`/ai/studio/preview/${token}/nudge`, { days }); toast.success(days === 0 ? "Reminders turned off" : days == null ? "Using default (3 days)" : `Reminder set for ${days} days`); await loadStats(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };
  const downloadPdf = async (t) => {
    try {
      const res = await api.get(`/ai/studio/preview/${t}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data); const a = document.createElement("a"); a.href = url; a.download = "website-preview.pdf"; a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const delVersion = async (t) => {
    if (!window.confirm("Delete this preview version? Its share link will stop working.")) return;
    try { await api.post(`/ai/studio/preview/${t}/delete`); toast.success("Preview deleted"); await loadHistory(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const draftReply = async (fb) => {
    setDrafts((d) => ({ ...d, [fb.id]: { ...(d[fb.id] || {}), open: true, loading: true } }));
    try {
      const { data } = await api.post(`/ai/studio/preview/${token}/feedback/${fb.id}/draft-reply`);
      setDrafts((d) => ({ ...d, [fb.id]: { open: true, loading: false, text: data.draft || "", cost: data.cost_estimated } }));
    } catch (e) { toast.error(formatApiError(e)); setDrafts((d) => ({ ...d, [fb.id]: { ...(d[fb.id] || {}), loading: false } })); }
  };
  const openReply = (fb) => setDrafts((d) => ({ ...d, [fb.id]: { ...(d[fb.id] || {}), open: true, text: d[fb.id]?.text || "" } }));
  const sendReply = async (fb) => {
    const dr = drafts[fb.id];
    if (!dr?.text?.trim()) { toast.error("Reply is empty"); return; }
    setDrafts((d) => ({ ...d, [fb.id]: { ...dr, sending: true } }));
    try {
      await api.post(`/ai/studio/preview/${token}/feedback/${fb.id}/reply`, { message: dr.text });
      toast.success("Reply sent to client");
      setDrafts((d) => ({ ...d, [fb.id]: { ...dr, sending: false, open: false } }));
      await loadStats();
    } catch (e) { toast.error(formatApiError(e)); setDrafts((d) => ({ ...d, [fb.id]: { ...dr, sending: false } })); }
  };
  const insertTemplate = (fb, body) => { setDrafts((d) => ({ ...d, [fb.id]: { ...(d[fb.id] || {}), open: true, text: body } })); setTplOpenFor(null); };
  const [tplPrompt, setTplPrompt] = useState(null);
  const saveTemplate = (fb) => {
    const dr = drafts[fb.id];
    if (!dr?.text?.trim()) { toast.error("Nothing to save"); return; }
    setTplPrompt({ body: dr.text });
  };
  const doSaveTemplate = async (title) => {
    const body = tplPrompt?.body; setTplPrompt(null);
    try { await api.post(`/ai/studio/reply-templates`, { title: title.trim(), body }); toast.success("Template saved"); loadTpls(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const delTemplate = async (id) => {
    try { await api.post(`/ai/studio/reply-templates/${id}/delete`); loadTpls(); } catch (e) { toast.error(formatApiError(e)); }
  };

  const opened = stats?.client_opened_at;
  const feedback = stats?.feedback || [];

  return (
    <div className="space-y-3 border-t border-white/10 pt-3" data-testid="wb-preview-manager">
      {/* Stat chips */}
      <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="wb-preview-stats">
        {stats?.version != null && <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-gold/40 text-gold">v{stats.version}</span>}
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border ${opened ? "border-emerald-500/40 text-emerald-300" : "border-white/15 text-gray-400"}`} data-testid="wb-stat-opened">
          <Eye size={12} /> {opened ? `Client opened · ${fmt(opened)}` : "Not opened by client yet"}
        </span>
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-white/15 text-gray-400"><span className="font-semibold text-gray-200">{stats?.views ?? 0}</span> views</span>
        {stats?.expired && <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-red-500/40 text-red-300">Expired</span>}
        <button onClick={() => downloadPdf(token)} data-testid="wb-download-pdf" className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-white/15 text-gray-200 hover:border-gold/50"><FileDown size={12} /> PDF</button>
        <button onClick={loadStats} data-testid="wb-refresh-stats" className="inline-flex items-center gap-1 text-gray-500 hover:text-white ml-auto"><RefreshCw size={12} /> Refresh</button>
      </div>

      {/* Controls row */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <button onClick={openSwap} data-testid="wb-swap-toggle" className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-white/15 text-gray-200 hover:border-gold/50"><ImageIcon size={13} /> Swap hero & logo</button>
        <div className="inline-flex items-center gap-1.5 text-xs text-gray-400">
          <CalendarClock size={13} className="text-gold" /> Expires
          <input type="date" value={expiry} onChange={(e) => setExpiry(e.target.value)} data-testid="wb-expiry-input" className="bg-navy border border-white/20 text-white px-2 py-1 text-xs focus:border-gold focus:outline-none" />
          <button onClick={saveExpiry} disabled={busy === "expiry"} data-testid="wb-expiry-save" className="inline-flex items-center gap-1 px-2 py-1 border border-gold/50 text-gold hover:bg-gold/10 disabled:opacity-50">{busy === "expiry" ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}</button>
        </div>
        <div className="inline-flex items-center gap-1.5 text-xs text-gray-400" title="Days after sending before an unopened-preview reminder goes out. 0 = off. Blank = default (3).">
          <BellRing size={13} className="text-gold" /> Remind after
          <input type="number" min="0" max="60" value={nudgeDays} onChange={(e) => setNudgeDays(e.target.value)} placeholder="3" data-testid="wb-nudge-input" className="w-14 bg-navy border border-white/20 text-white px-2 py-1 text-xs focus:border-gold focus:outline-none" />
          days
          <button onClick={saveNudge} disabled={busy === "nudge"} data-testid="wb-nudge-save" className="inline-flex items-center gap-1 px-2 py-1 border border-gold/50 text-gold hover:bg-gold/10 disabled:opacity-50">{busy === "nudge" ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}</button>
        </div>
        {projectId && history.length > 1 && (
          <button onClick={() => setCompare({ a: history[0]?.token, b: history[1]?.token })} data-testid="wb-compare-open" className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-white/15 text-gray-200 hover:border-gold/50"><Columns2 size={13} /> Compare versions</button>
        )}
      </div>

      {/* Swap panel (hero + logo) */}
      {showSwap && (
        <div className="bg-navy border border-white/10 p-3 space-y-3" data-testid="wb-swap-panel">
          <div className="text-[11px] uppercase tracking-wide text-gray-500">Hero image</div>
          <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
            {opts === null ? <div className="col-span-full text-center text-gray-500 text-xs py-3"><Loader2 size={14} className="animate-spin inline mr-1" /> Loading options…</div>
              : opts.map((o, i) => (
                <button key={i} onClick={() => applyHero(o.url)} disabled={busy === "hero"} data-testid={`wb-hero-option-${i}`}
                  className={`relative rounded overflow-hidden border transition-colors ${stats?.hero === o.url ? "border-gold ring-1 ring-gold" : "border-white/10 hover:border-gold/50"}`}>
                  <img src={o.thumb} alt="" className="w-full h-14 object-cover" />
                  {stats?.hero === o.url && <span className="absolute inset-0 bg-gold/20 flex items-center justify-center"><Check size={16} className="text-white drop-shadow" /></span>}
                </button>
              ))}
          </div>
          <div className="flex gap-2">
            <input value={customUrl} onChange={(e) => setCustomUrl(e.target.value)} placeholder="…or paste your own hero image URL (https://…)" data-testid="wb-hero-url" className="flex-1 bg-navy-800 border border-white/20 text-white px-2.5 py-1.5 text-xs focus:border-gold focus:outline-none" />
            <button onClick={() => applyHero(customUrl)} disabled={busy === "hero"} data-testid="wb-hero-url-apply" className="inline-flex items-center gap-1 px-3 py-1.5 text-xs border border-gold/50 text-gold hover:bg-gold/10 disabled:opacity-50">{busy === "hero" ? <Loader2 size={12} className="animate-spin" /> : "Use"}</button>
          </div>
          <div className="text-[11px] uppercase tracking-wide text-gray-500 pt-1">Logo {stats?.logo ? <span className="text-emerald-400 normal-case">· set</span> : <span className="text-gray-600 normal-case">· using project logo if available</span>}</div>
          <div className="flex items-center gap-2">
            {stats?.logo && <img src={stats.logo} alt="logo" className="h-8 w-auto object-contain bg-white/5 rounded px-1" data-testid="wb-logo-preview" />}
            <input value={logoUrl} onChange={(e) => setLogoUrl(e.target.value)} placeholder="Paste a logo image URL to override…" data-testid="wb-logo-url" className="flex-1 bg-navy-800 border border-white/20 text-white px-2.5 py-1.5 text-xs focus:border-gold focus:outline-none" />
            <button onClick={() => applyLogo(logoUrl)} disabled={busy === "logo"} data-testid="wb-logo-apply" className="inline-flex items-center gap-1 px-3 py-1.5 text-xs border border-gold/50 text-gold hover:bg-gold/10 disabled:opacity-50">{busy === "logo" ? <Loader2 size={12} className="animate-spin" /> : "Use"}</button>
            {stats?.logo && <button onClick={() => applyLogo("")} data-testid="wb-logo-clear" className="text-gray-500 hover:text-red-400" title="Clear logo"><Trash2 size={14} /></button>}
          </div>
        </div>
      )}

      {/* Feedback list + AI reply */}
      {feedback.length > 0 && (
        <div className="bg-navy border border-white/10 p-3 space-y-3" data-testid="wb-feedback-list">
          <div className="flex items-center gap-1.5 text-xs text-gray-400"><MessageSquare size={13} className="text-gold" /> Client feedback ({feedback.length})</div>
          {feedback.map((f) => {
            const dr = drafts[f.id] || {};
            return (
              <div key={f.id} className="text-xs border-b border-white/5 pb-2 last:border-0" data-testid={`wb-feedback-${f.id}`}>
                <div className="flex items-start gap-2">
                  {f.sentiment === "love" ? <ThumbsUp size={13} className="text-emerald-400 mt-0.5 shrink-0" /> : <PencilLine size={13} className="text-amber-400 mt-0.5 shrink-0" />}
                  <div className="flex-1">
                    <div className="text-gray-200">{f.sentiment === "love" ? "Loved it" : "Requested changes"}{f.name ? ` · ${f.name}` : ""}{f.email ? ` · ${f.email}` : ""}</div>
                    {f.message && <div className="text-gray-400 mt-0.5">“{f.message}”</div>}
                    <div className="text-gray-600 mt-0.5">{fmt(f.at)}{f.replied_at && <span className="text-emerald-400"> · replied {fmt(f.replied_at)}</span>}</div>
                  </div>
                  {f.email && !f.replied_at && (
                    <div className="flex items-center gap-1 shrink-0">
                      <button onClick={() => draftReply(f)} disabled={dr.loading} data-testid={`wb-draft-reply-${f.id}`}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-gold/50 text-gold hover:bg-gold/10 disabled:opacity-50">
                        {dr.loading ? <Loader2 size={12} className="animate-spin" /> : <Wand2 size={12} />} Draft
                      </button>
                      <div className="relative">
                        <button onClick={() => { if (tpls === null) loadTpls(); setTplOpenFor(tplOpenFor === f.id ? null : f.id); }} data-testid={`wb-tpl-toggle-${f.id}`} className="inline-flex items-center gap-0.5 px-2 py-1 text-xs border border-white/15 text-gray-300 hover:border-gold/50">Templates <ChevronDown size={11} /></button>
                        {tplOpenFor === f.id && (
                          <div className="absolute right-0 mt-1 w-56 bg-navy-800 border border-white/15 rounded shadow-xl z-20 p-1" data-testid={`wb-tpl-menu-${f.id}`}>
                            {tpls === null ? <div className="text-gray-500 text-xs px-2 py-2">Loading…</div>
                              : tpls.length === 0 ? <div className="text-gray-500 text-xs px-2 py-2">No templates yet. Draft a reply and “Save as template”.</div>
                                : tpls.map((t) => (
                                  <div key={t.id} className="flex items-center gap-1 group">
                                    <button onClick={() => insertTemplate(f, t.body)} data-testid={`wb-tpl-use-${t.id}`} className="flex-1 text-left text-xs text-gray-200 hover:text-gold px-2 py-1.5 truncate">{t.title}</button>
                                    <button onClick={() => delTemplate(t.id)} className="opacity-0 group-hover:opacity-100 text-gray-500 hover:text-red-400 px-1"><Trash2 size={11} /></button>
                                  </div>
                                ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                {dr.open && !dr.loading && (
                  <div className="mt-2 space-y-2" data-testid={`wb-reply-editor-${f.id}`}>
                    <textarea value={dr.text || ""} onChange={(e) => setDrafts((d) => ({ ...d, [f.id]: { ...dr, text: e.target.value } }))} rows={5}
                      className="w-full bg-navy-800 border border-white/15 text-gray-100 p-2.5 text-xs leading-relaxed focus:border-gold focus:outline-none" />
                    <div className="flex items-center gap-2 flex-wrap">
                      {dr.cost != null && <span className="text-[11px] text-gray-500">AI draft cost: ${Number(dr.cost || 0).toFixed(4)} est</span>}
                      <button onClick={() => saveTemplate(f)} data-testid={`wb-save-tpl-${f.id}`} className="inline-flex items-center gap-1 px-2 py-1.5 text-xs border border-white/15 text-gray-300 hover:border-gold/50"><BookmarkPlus size={12} /> Save as template</button>
                      <button onClick={() => sendReply(f)} disabled={dr.sending} data-testid={`wb-send-reply-${f.id}`} className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-gold text-navy font-semibold hover:bg-gold-light disabled:opacity-50">{dr.sending ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Send reply to client</button>
                    </div>
                  </div>
                )}
                {f.email && !f.replied_at && !dr.open && (
                  <button onClick={() => openReply(f)} className="text-[11px] text-gray-500 hover:text-gold mt-1 ml-5">Write manually</button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Version history */}
      {projectId && history.length > 1 && (
        <div className="bg-navy border border-white/10 p-3 space-y-2" data-testid="wb-preview-history">
          <div className="flex items-center gap-1.5 text-xs text-gray-400"><History size={13} className="text-gold" /> Preview versions ({history.length})</div>
          {history.map((h) => (
            <div key={h.token} className={`flex items-center gap-2 text-xs py-1 ${h.token === token ? "text-white" : "text-gray-400"}`} data-testid={`wb-history-${h.token}`}>
              <span className="font-semibold w-10">v{h.version}</span>
              <span className="text-gray-600">{fmt(h.created_at)}</span>
              {h.token === token && <span className="text-gold text-[10px] border border-gold/40 rounded-full px-1.5">current</span>}
              <span className="ml-auto flex items-center gap-3">
                <span className={h.client_opened_at ? "text-emerald-400" : "text-gray-600"}>{h.client_opened_at ? "opened" : "not opened"}</span>
                <span>{h.views} views</span>
                {h.feedback_count > 0 && <span className="text-gold">{h.feedback_count} fb</span>}
                <button onClick={() => downloadPdf(h.token)} className="text-gray-400 hover:text-white" title="PDF"><FileDown size={13} /></button>
                <a href={`${ORIGIN}/preview/${h.token}`} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white" title="Open"><ExternalLink size={13} /></a>
                <button onClick={() => delVersion(h.token)} className="text-gray-500 hover:text-red-400" data-testid={`wb-history-delete-${h.token}`} title="Delete"><Trash2 size={13} /></button>
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Compare modal */}
      {compare && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setCompare(null)} data-testid="wb-compare-modal">
          <div className="bg-navy-800 border border-white/10 w-full max-w-6xl h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-3 border-b border-white/10">
              <div className="text-white text-sm font-medium flex items-center gap-2"><Columns2 size={15} className="text-gold" /> Compare preview versions</div>
              <button onClick={() => setCompare(null)} data-testid="wb-compare-close" className="text-gray-400 hover:text-white"><X size={18} /></button>
            </div>
            <div className="grid grid-cols-2 gap-px bg-white/10 flex-1 overflow-hidden">
              {["a", "b"].map((side) => (
                <div key={side} className="bg-navy-800 flex flex-col">
                  <select value={compare[side] || ""} onChange={(e) => setCompare((c) => ({ ...c, [side]: e.target.value }))} data-testid={`wb-compare-select-${side}`} className="bg-navy border-b border-white/10 text-white text-xs p-2 focus:outline-none">
                    {history.map((h) => <option key={h.token} value={h.token}>v{h.version} · {fmt(h.created_at)}</option>)}
                  </select>
                  {compare[side] ? <iframe title={`compare-${side}`} src={`${ORIGIN}/preview/${compare[side]}`} className="flex-1 w-full bg-white" data-testid={`wb-compare-frame-${side}`} /> : <div className="flex-1 flex items-center justify-center text-gray-500 text-xs">Pick a version</div>}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      <PromptDialog open={!!tplPrompt} title="Save reply template" label="Template name"
        placeholder="e.g. Friendly follow-up" confirmText="Save template"
        onSubmit={doSaveTemplate} onClose={() => setTplPrompt(null)} />
    </div>
  );
}
